import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';

import { Button, TextButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { SegmentPage } from '../../misc/segment/segment-page';
import { Input } from './Input';
import { editProfileBunja } from './bunja';
import { SocialXSection } from './social/x-section';

interface EditProfileFormProps {
  onClose: () => void;
  onSubmit: () => void;
  setBothToPublic: () => void;
}
const EditProfileForm = ({ onClose, onSubmit, setBothToPublic }: EditProfileFormProps) => {
  const {
    socialLinkProvidersAtom,
    usernameAtom,
    usernameValidationAtom,
    socialLinksAtom,
    strippedSocialLinksAtom,
    socialLinksValidationAtom,
    showBothToPublicSectionAtom,
    pristineAtom,
    validationAtom,
    initialSocialConnectionSegmentAtom,
  } = useBunja(editProfileBunja);
  const socialLinkProviders = useAtomValue(socialLinkProvidersAtom);
  const [username, setUsername] = useAtom(usernameAtom);
  const usernameValidation = useAtomValue(usernameValidationAtom);
  const [socialLinks, setSocialLinks] = useAtom(socialLinksAtom);
  const socialLinksValidation = useAtomValue(socialLinksValidationAtom);
  const showBothToPublicSection = useAtomValue(showBothToPublicSectionAtom);
  const strippedSocialLinks = useAtomValue(strippedSocialLinksAtom);
  const pristine = useAtomValue(pristineAtom);
  const valid = useAtomValue(validationAtom);
  const initialContext = useAtomValue(initialSocialConnectionSegmentAtom);
  return (
    <>
      <SegmentPage name={ProfileEvent.EditProfilePageView} context={initialContext} />
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label='Edit profile' />
      </Modal.Header>
      <Modal.Content>
        <section className={stack({ gap: 40 })}>
          <div className={stack({ gap: 12 })}>
            <label htmlFor='username' className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>
              Username
            </label>
            <div className={stack({ gap: 4 })}>
              <Input
                value={username}
                onChange={(e) => setUsername(e.currentTarget.value)}
                onXClick={username ? () => setUsername('') : undefined}
                message={'Create a username between 3 and 17 characters.\nYou can change it once every 30 days.'}
                error={usernameValidation.message}
              />
            </div>
          </div>
          <div className={stack({ gap: 12 })}>
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>
              {socialLinkProviders.length > 1 ? 'Social links' : 'Social link'}
            </p>
            <div className={stack({ gap: 24 })}>
              {showBothToPublicSection && (
                <div className={hstack({ gap: 12 })}>
                  <div>
                    <LockClosedIcon size={20} color={token('colors.content.default.level-4')} />
                  </div>
                  <div className={stack({ gap: 4 })}>
                    <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      Set both Performance and Trading activity to public to display your social links.
                    </p>
                    <div>
                      <TextButton
                        label='Set both to public'
                        onClick={() => {
                          segment.track(ProfileEvent.SwitchToPublicLinkClick);
                          setBothToPublic();
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className={stack({ gap: 12 })}>
                {socialLinkProviders.map(({ provider, label }) => (
                  <Input
                    key={provider}
                    name={provider}
                    label={label}
                    value={socialLinks[provider]}
                    onChange={(e) => setSocialLinks(provider, e.currentTarget.value)}
                    onXClick={strippedSocialLinks[provider] ? () => setSocialLinks(provider, '') : undefined}
                    error={!socialLinksValidation[provider] && 'Invalid link. Please check the format.'}
                  />
                ))}
              </div>
              <SocialXSection />
            </div>
          </div>
        </section>
      </Modal.Content>
      <Modal.Footer>
        <Button label='Save' size='large' disabled={pristine || !valid} onClick={onSubmit} withSpinner />
      </Modal.Footer>
    </>
  );
};

export default EditProfileForm;
